import { DEFAULT_BRAND } from "./defaultBrand";
import { getBrand, useBrand } from "./store";
import type { Brand } from "./types";

/**
 * OEM 贴牌 · 人设名(W2-con-persona,设计稿 设计-console.md §0 / §2.2)。
 *
 * 取值优先级:**租户 advisorName > 品牌 persona > 默认(DEFAULT_BRAND.persona)**。
 * - title = 称呼位(直营「军师」)。租户 advisorName 占的就是这一位(老板给自己 AI 起的称呼)。
 * - name  = 名字位(直营「小阳」)。租户 advisorName 不覆盖它。
 *
 * ★租户 advisorName 只在调用方显式传入时生效(= 改造前 advisorNameFrom 用到的那几处:
 *   H5 对话顶栏 / 头像首字 / 输入框占位 / 思考中提示 / 海报页)。全站 i18n 变量
 *   {{personaTitle}} 只跟品牌走,不吃租户名 —— 否则「经营军师」这类功能名会被租户名改掉,
 *   与改造前行为不一致。
 *
 * 文案优先走 i18n 变量({{personaTitle}} / {{personaName}} / {{personaFull}});
 * 写在 TSX 里的对客字面量:组件用 `usePersona()`,非组件 / 模块级常量用 `getPersona()`
 * (模块级常量必须改成函数现取 —— 模块求值早于 bootstrapBrand)。
 */
export interface Persona {
  /** 称呼位(直营「军师」)。 */
  title: string;
  /** 名字位(直营「小阳」)。 */
  name: string;
}

function pick(...candidates: Array<string | null | undefined>): string {
  for (const c of candidates) {
    const v = c?.trim();
    if (v) return v;
  }
  return "";
}

/** 纯函数:给定品牌 + 可选租户 advisorName,算出人设名。 */
export function resolvePersona(brand: Brand, tenantAdvisorName?: string | null): Persona {
  return {
    title: pick(tenantAdvisorName, brand.persona?.title, DEFAULT_BRAND.persona.title),
    name: pick(brand.persona?.name, DEFAULT_BRAND.persona.name),
  };
}

/** 非组件代码取人设名(每次现读当前品牌)。 */
export function getPersona(tenantAdvisorName?: string | null): Persona {
  return resolvePersona(getBrand(), tenantAdvisorName);
}

/** 组件取人设名(品牌变化会重渲)。 */
export function usePersona(tenantAdvisorName?: string | null): Persona {
  return resolvePersona(useBrand(), tenantAdvisorName);
}

/**
 * 1004 O5:后端目录数据里写死的直营人设名 → 当前品牌人设名。
 *
 * xb_module_catalog 的 display_name / description 是按直营人设写的(advisor 能力名、经营看板简介里的名字位),
 * 渠道租户首页卡墙 / 帮助页 / 我的套餐 / 资源上限直接渲染这些字段就把直营人设露出去了。
 * 凡是**后端目录类文案**(不是用户自己写的内容)进页面前过一遍这里;直营品牌人设与默认相同 → 原样返回。
 * ★只换目录文案,不碰用户内容(知识库 / 对话 / 作品标题):那些是客户自己的字,改了还会被回写。
 * ★不吃租户 advisorName(与 {{personaTitle}} 同口径,见文件头)。
 */
export function brandText<T extends string | null | undefined>(
  text: T,
  persona: Persona = getPersona()
): T {
  if (!text) return text;
  const def = DEFAULT_BRAND.persona;
  let out: string = text;
  if (persona.name && persona.name !== def.name) out = out.split(def.name).join(persona.name);
  if (persona.title && persona.title !== def.title) out = out.split(def.title).join(persona.title);
  return out as T;
}

/** 对一组后端目录行的指定文本字段套 {@link brandText}(返回新数组,不改原对象)。 */
export function brandRows<T extends object>(
  rows: T[] | null | undefined,
  ...keys: Array<keyof T>
): T[] {
  if (!rows) return [];
  const persona = getPersona();
  return rows.map((r) => {
    const copy = { ...r };
    for (const k of keys) {
      const v = copy[k];
      if (typeof v === "string") (copy as Record<keyof T, unknown>)[k] = brandText(v, persona);
    }
    return copy;
  });
}
